import React from "react";

import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";

function ConfirmationRow({
  checked,
  disabled,
  title,
  description,
  testId,
  onChange,
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
        data-testid={testId}
        className="mt-1 h-4 w-4 shrink-0 accent-[var(--do-accent)]"
      />

      <span className="min-w-0">
        <strong className="block text-sm text-[var(--do-text)]">
          {title}
        </strong>
        <span className="mt-1 block text-xs text-[var(--do-muted)]">
          {description}
        </span>
      </span>
    </label>
  );
}

export default function InstallationArchiveModal({
  open,
  loading = false,
  saving = false,
  dependencies = {},
  reason = "",
  releaseMachineReservation = false,
  cancelIntervention = false,
  onReasonChange,
  onReleaseMachineChange,
  onCancelInterventionChange,
  onClose,
  onSubmit,
}) {
  const machine = dependencies.machine || {};
  const intervention = dependencies.intervention || {};

  const releaseRequired = Boolean(machine.releaseRequired);
  const cancelRequired = Boolean(intervention.cancelRequired);

  const canSubmit =
    Boolean(reason.trim())
    && !loading
    && !saving
    && (!releaseRequired || releaseMachineReservation)
    && (!cancelRequired || cancelIntervention);

  return (
    <ActaModal
      open={open}
      onClose={onClose}
      closeDisabled={saving}
      eyebrow="Installation"
      title="Supprimer l’installation"
      subtitle="L’installation sera conservée dans Terminées comme archive. Le motif restera enregistré."
      size="md"
      testId="installation-archive-modal"
      footer={(
        <>
          <Button
            type="button"
            variant="actaSecondary"
            disabled={saving}
            onClick={onClose}
          >
            Annuler
          </Button>

          <Button
            type="button"
            variant="actaDanger"
            disabled={!canSubmit}
            onClick={onSubmit}
            data-testid="installation-archive-submit"
          >
            {saving ? "Suppression…" : "Supprimer et archiver"}
          </Button>
        </>
      )}
    >
      {loading ? (
        <p className="text-sm text-[var(--do-muted)]">
          Vérification des éléments liés…
        </p>
      ) : (
        <div className="space-y-4">
          <label className="block space-y-2">
            <span className="label-eyebrow block">
              Motif de suppression *
            </span>
            <textarea
              value={reason}
              onChange={(event) => onReasonChange?.(event.target.value)}
              disabled={saving}
              rows={4}
              maxLength={1000}
              autoFocus
              data-testid="installation-archive-reason"
              placeholder="Indiquez pourquoi cette installation est supprimée."
              className="w-full resize-none rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
            />
          </label>

          {releaseRequired ? (
            <ConfirmationRow
              checked={releaseMachineReservation}
              disabled={saving}
              title="Libérer la réservation de la machine"
              description={[
                machine.name || "Machine réservée",
                machine.serialNumber ? `N° ${machine.serialNumber}` : "",
                "Elle reviendra dans le stock disponible.",
              ].filter(Boolean).join(" · ")}
              testId="installation-archive-release-machine"
              onChange={onReleaseMachineChange}
            />
          ) : null}

          {cancelRequired ? (
            <ConfirmationRow
              checked={cancelIntervention}
              disabled={saving}
              title="Annuler l’intervention liée"
              description={
                intervention.status
                  ? `Intervention actuellement « ${intervention.status} ». Elle sera annulée avec le motif de suppression.`
                  : "L’intervention liée sera annulée."
              }
              testId="installation-archive-cancel-intervention"
              onChange={onCancelInterventionChange}
            />
          ) : null}

          {!releaseRequired && !cancelRequired ? (
            <p className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3 text-sm text-[var(--do-muted)]">
              Aucun élément lié ne nécessite d’action supplémentaire.
            </p>
          ) : null}
        </div>
      )}
    </ActaModal>
  );
}
